import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Dog, Calendar as CalendarIcon, Search, LogOut, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { mockDogs, mockAnnouncements, turkishProvinces } from '../../utils/mockData';
import { toast } from 'sonner';

const Profile = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [myDogs, setMyDogs] = useState([]);
  const [breedings, setBreedings] = useState([]);

  useEffect(() => {
    const mockUser = localStorage.getItem('mockUser');
    if (!mockUser) {
      navigate('/portal/login');
    } else {
      setUser(JSON.parse(mockUser));
      // Load mock dogs for this user
      setMyDogs([
        { id: 1, name: 'CAESAR', skbNo: 'TR-123456', color: 'Black', birthDate: '2020-05-10', titles: 'CH TR', gender: 'male' },
        { id: 2, name: 'BELLA', skbNo: 'TR-234567', color: 'Blue', birthDate: '2021-03-15', titles: 'CH TR', gender: 'female' }
      ]);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('mockUser');
    toast.success('Çıkış yapıldı');
    navigate('/');
  };

  const handleAddDog = () => {
    toast.info('Köpek ekleme özelliği backend entegrasyonu ile aktif olacak');
  };

  const handleAddBreeding = () => {
    toast.info('Eşleşme ekleme özelliği backend entegrasyonu ile aktif olacak');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900">{t('portal.profile.title')}</h1>
            <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-600 hover:bg-red-50">
              <LogOut size={18} className="mr-2" />
              {t('portal.profile.logout')}
            </Button>
          </div>

          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start mb-8 bg-white p-2 rounded-lg shadow-md">
              <TabsTrigger value="personal" className="px-4 py-2 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                <User size={18} className="mr-2" />
                {t('portal.profile.personalInfo')}
              </TabsTrigger>
              <TabsTrigger value="dogs" className="px-4 py-2 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                <Dog size={18} className="mr-2" />
                {t('portal.profile.myDogs')}
              </TabsTrigger>
              <TabsTrigger value="breedings" className="px-4 py-2 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                <CalendarIcon size={18} className="mr-2" />
                {t('portal.profile.plannedBreedings')}
              </TabsTrigger>
              <TabsTrigger value="search" className="px-4 py-2 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                <Search size={18} className="mr-2" />
                {t('portal.profile.dogSearch')}
              </TabsTrigger>
            </TabsList>

            {/* Personal Info Tab */}
            <TabsContent value="personal">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.name')}
                    </label>
                    <Input value={user.name} disabled />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('portal.profile.birthDate')}
                    </label>
                    <Input type="date" placeholder="Doğum tarihinizi seçin" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('portal.profile.city')}
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="İl seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {turkishProvinces.map((province) => (
                          <SelectItem key={province} value={province}>{province}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <Input value="(555) 123 45 67" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')}
                    </label>
                    <Input value={user.email} disabled />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('portal.profile.fciBreederName')}
                    </label>
                    <Input placeholder="FCI üretici isminiz" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('portal.profile.fciBreederNo')}
                    </label>
                    <Input placeholder="FCI kayıt numaranız" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('breeders.website')}
                    </label>
                    <Input placeholder="www.example.com" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instagram
                    </label>
                    <Input placeholder="@kullaniciadi" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facebook
                    </label>
                    <Input placeholder="Kullanıcı adı" />
                  </div>
                </div>

                <div className="mt-6">
                  <Button className="bg-red-600 hover:bg-red-700">
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* My Dogs Tab */}
            <TabsContent value="dogs">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{t('portal.profile.myDogs')}</h2>
                  <Button onClick={handleAddDog} className="bg-red-600 hover:bg-red-700">
                    <Plus size={18} className="mr-2" />
                    {t('portal.profile.addDog')}
                  </Button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">{t('portal.profile.males')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {myDogs.filter(dog => dog.gender === 'male').map((dog) => (
                        <div key={dog.id} className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                          <h4 className="font-semibold text-lg text-gray-900">{dog.name}</h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p><strong>SKB No:</strong> {dog.skbNo}</p>
                            <p><strong>Renk:</strong> {dog.color}</p>
                            <p><strong>Doğum Tarihi:</strong> {new Date(dog.birthDate).toLocaleDateString()}</p>
                            <p><strong>Ünvanlar:</strong> {dog.titles}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">{t('portal.profile.females')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {myDogs.filter(dog => dog.gender === 'female').map((dog) => (
                        <div key={dog.id} className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                          <h4 className="font-semibold text-lg text-gray-900">{dog.name}</h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p><strong>SKB No:</strong> {dog.skbNo}</p>
                            <p><strong>Renk:</strong> {dog.color}</p>
                            <p><strong>Doğum Tarihi:</strong> {new Date(dog.birthDate).toLocaleDateString()}</p>
                            <p><strong>Ünvanlar:</strong> {dog.titles}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Planned Breedings Tab */}
            <TabsContent value="breedings">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{t('portal.profile.plannedBreedings')}</h2>
                  <Button onClick={handleAddBreeding} className="bg-red-600 hover:bg-red-700">
                    <Plus size={18} className="mr-2" />
                    {t('portal.profile.addBreeding')}
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('portal.profile.selectMale')}
                      </label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Erkek köpek seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {myDogs.filter(dog => dog.gender === 'male').map((dog) => (
                            <SelectItem key={dog.id} value={dog.id.toString()}>{dog.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('portal.profile.selectFemale')}
                      </label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Dişi köpek seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {myDogs.filter(dog => dog.gender === 'female').map((dog) => (
                            <SelectItem key={dog.id} value={dog.id.toString()}>{dog.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('portal.profile.estimatedMonth')} / {t('portal.profile.estimatedYear')}
                      </label>
                      <Input type="month" />
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-600 text-sm">
                    Henüz planlanmış eşleşme bulunmamaktadır. Yukarıdaki formu kullanarak yeni eşleşme planı ekleyebilirsiniz.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Dog Search Tab */}
            <TabsContent value="search">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">{t('portal.profile.dogSearch')}</h2>
                <p className="text-gray-600 mb-6">{t('portal.profile.searchAvailable')}</p>

                <div className="space-y-4">
                  {mockDogs.filter(dog => dog.availableForMating).map((dog) => (
                    <div key={dog.id} className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">{dog.name}</h3>
                          <p className="text-gray-600 mt-1">{dog.titles}</p>
                          <div className="mt-3 space-y-1 text-sm text-gray-600">
                            <p><strong>SKB No:</strong> {dog.skbNo}</p>
                            <p><strong>Renk:</strong> {dog.color}</p>
                            <p><strong>Doğum Tarihi:</strong> {new Date(dog.birthDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                          {t('portal.profile.available')}
                        </span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-700"><strong>{t('portal.profile.ownerInfo')}:</strong></p>
                        <p className="text-sm text-gray-600">{dog.owner} - {dog.phone}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Announcements Section */}
          <div className="mt-8 bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">{t('portal.profile.announcements')}</h2>
            <div className="space-y-4">
              {mockAnnouncements.slice(0, 3).map((announcement) => (
                <div key={announcement.id} className="p-4 bg-red-50 border-l-4 border-red-600 rounded-r-lg">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-gray-900">
                      {announcement[`title${currentLang.charAt(0).toUpperCase() + currentLang.slice(1)}`]}
                    </h3>
                    <span className="text-sm text-gray-600">
                      {new Date(announcement.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm mt-2">{announcement.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
